import { type LoaderFunctionArgs, Navigate, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { datasetOrgId } from "@/shared/domain/datasets/loaders/datasetOrgId";
import { hasLegacyEvents, legacyEventsWritable } from "@/shared/domain/orgs";
import { QueryTarget } from "@/shared/domain/query";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { DatasetTab, Goto } from "@/shared/navigation";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import { orgEventModelRoute } from "@/shared/routing/orgEventModelRoute";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { CreateDataset } from "./CreateDataset";
import { CreateDatasetEvent } from "./CreateDatasetEvent";
import { Dataset } from "./Dataset";
import { DatasetAgentThreads } from "./Dataset/DatasetAgentThreads";
import { DatasetDetails } from "./Dataset/DatasetDetails";
import { DatasetEvents } from "./Dataset/DatasetEvents";
import { DatasetFileBrowser } from "./Dataset/DatasetFileBrowser";
import { DatasetInvocations } from "./Dataset/DatasetInvocations";
import { DatasetPermissions } from "./Dataset/DatasetPermissions";
import { DatasetSessions } from "./Dataset/DatasetSessions";
import { DatasetAISummary } from "./Dataset/DatasetSummary";
import { DatasetTopics } from "./Dataset/DatasetTopics";
import { DatasetsList } from "./DatasetsList";
import { loadDatasetByName } from "./loaders";

// The list blocks on two independent reads: its saved table preferences, and —
// only when the URL names one — the View it should open with. Resolving the
// View here is what lets `useViewFromLink` build the table from it on the
// first render, instead of the table arriving unfiltered and being corrected.
const datasetsListLoader = composeLoaders(
  loadSearchTablePreferences(queryClient, "dataset_search_table_preferences"),
  makeViewsLoader(queryClient, { target: QueryTarget.Datasets }),
);

// The dataset event routes below redirect to the Files tab only when the org's
// record says its dataset events are gone (or, for the create page, read-only).
// When no org record loads (`undefined`) they stay open, and the server refuses
// any change the org cannot make.
const redirectToDatasetFiles = ({ params }: LoaderFunctionArgs) =>
  Goto.createDatasetTabRoute(params.datasetPath ?? "", DatasetTab.Files);

export const DatasetsRoutes = () => [
  <Route
    key={"/"}
    index
    loader={datasetsListLoader}
    element={<DatasetsList />}
  />,
  <Route key={"create"} path="create" element={<CreateDataset />} />,
  <Route
    key={"name"}
    path="name/:datasetName"
    loader={loadDatasetByName}
    shouldRevalidate={({ currentUrl, nextUrl }) => {
      return currentUrl.pathname !== nextUrl.pathname;
    }}
    // If the loader does not redirect to a dataset,
    // take the user to the dataset search page.
    element={<Navigate to="/search" replace />}
  />,
  orgEventModelRoute({
    path: ":datasetPath/events/create",
    allows: (model) => model === undefined || legacyEventsWritable(model),
    redirectTo: redirectToDatasetFiles,
    owningOrgId: datasetOrgId,
    element: <CreateDatasetEvent />,
  }),
  // Dataset detail view: parent layout with one child route per tab,
  // mounted via <Dataset />'s <Outlet>. <Dataset /> publishes the
  // dataset record, current org, and datasetId through outlet context,
  // which tabs read via `useDatasetContext` instead of re-deriving from
  // useParams/useAuth.
  <Route key={"id"} path=":datasetPath" element={<Dataset />}>
    <Route index element={<Navigate to={DatasetTab.Files} replace />} />
    <Route path={`${DatasetTab.Files}/*`} element={<DatasetFileBrowser />} />
    {/*
        Opening the Sessions tab must have saved column order / page size
        in cache before <DatasetSessions /> mounts: `useResourceView`
        snapshots `preferences` once via `React.useState` and never
        re-reads.
    */}
    <Route
      path={DatasetTab.Sessions}
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<DatasetSessions />}
    />
    <Route path={DatasetTab.Topics} element={<DatasetTopics />} />
    {orgEventModelRoute({
      path: DatasetTab.Events,
      allows: (model) => model === undefined || hasLegacyEvents(model),
      redirectTo: redirectToDatasetFiles,
      owningOrgId: datasetOrgId,
      element: <DatasetEvents />,
    })}
    <Route path={DatasetTab.Actions} element={<DatasetInvocations />} />
    <Route path={DatasetTab.AgentThreads} element={<DatasetAgentThreads />} />
    <Route path={DatasetTab.Details} element={<DatasetDetails />} />
    <Route path={DatasetTab.Permissions} element={<DatasetPermissions />} />
    <Route path={DatasetTab.AISummary} element={<DatasetAISummary />} />
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ datasetPath }) =>
            Goto.createDatasetTabRoute(datasetPath ?? "", DatasetTab.Files)
          }
        />
      }
    />
  </Route>,
];
